// ft3d-overrun.ts — BG3-style OVERRUN ("shove / pushback") indicator (frontend-only). Sibling of
// ft3d-oa-threat.ts: that one marks hostiles whose REACH the drag path would cross (opportunity attacks);
// this one marks hostiles whose SQUARE the drag path would pass THROUGH (overruns).
//
// Server truth mirrored: move_encounter_creature_logic.php's overrun pre-scan + handle_overrun_during_movement.php.
// In combat, against a cross-team, alive, non-private creature, a step whose mover rectangle OVERLAPS the
// hostile's rectangle AND presses DEEPER into that rectangle than the previous step triggers an Overrun.
// If the mover has 0 Overrun charges the step is BLOCKED and movement halts before the creature.
//
// While dragging an encounter creature, for each hostile the preview path would overrun we drop the existing
// `overrun` catalog icon on that token. When the mover has no overrun charges we additionally report
// `blocked` + a snap stop-point (the last cell before the blocking creature, where the server will halt) so
// the caller can paint the amber "no overrun charges" border on the drop preview and snap the ghost there.
//
// Driven from DragManager / TokenDragController (local drag) AND websocket/xz-move.ts (remote drag previews,
// so other users see it too). Both pass a `scope` key so decorations from different draggers don't collide.
//
// PUBLIC GLOBAL (window-attached at the bottom): FT3D_OVERRUN.

import { overrunChargesRemainingOf, encounterCreatureField, encounterField } from '../shared/array-types';

// Mirror of PHP overrun_negating_status_ids() (includes/functions/janus/encounter/overrun_status_gate.php).
// A creature carrying any of these cannot brace or reposition, so it cannot STOP an overrun — the mover
// passes straight through its square (no icon, no block, no snap). Keep this list in sync with the PHP one.
const CANNOT_STOP_OVERRUN_STATUS_IDS = [
    '457ADEAA-1D5D-9AB4-EFC9-2B7120C78BAA', // petrified
    'BF31EE0A-5B3C-370B-0118-96B067CFD929', // paralyzed
    'F73BA661-ADD2-26E0-E265-FBEB7E5E6AF1', // unconscious
    '095BC6BC-06F6-74E0-2B8A-FC1F32AF809B', // sleeping
    'D218F4FB-5693-87B9-93C1-776FC1F7ED90', // stunned
    '6EFBE105-7632-4C64-BC04-206CEAF663E6', // frozen
    '0C2A8037-B0BC-8300-C818-198881B00E7D', // grappled
    'E34AF232-5438-88FC-9A9F-73850CCCFD51', // restrained
];

interface GridPoint {
    x: number;
    z: number;
}

interface CreatureRect {
    left: number;
    right: number;
    bottom: number;
    top: number;
}

interface OverrunWidget {
    encounterArr?: unknown;
    getObjectRectangle(creature: unknown): CreatureRect;
}

interface OverrunCandidate {
    id: string;
    creatureObj: unknown;
    rect: CreatureRect;
    triggerStep: number; // -1 until the path first overlaps+presses into this hostile
}

interface OverrunResult {
    overrunActive: boolean;
    blocked: boolean;
    snap: GridPoint | null;
    ids: string[]; // creature ids decorated this frame (broadcast to remote viewers)
}

declare global {
    interface Window {
        FT3D_OVERRUN: {
            update(widget: OverrunWidget, moverObj: unknown, origin: GridPoint, preview: GridPoint, scope?: string): OverrunResult;
            // Remote path: paint a known set of overrun targets (from the broadcast) without recomputing
            // geometry — the sender already snapped its preview so local recomputation would miss them.
            decorate(ids: string[], blocked: boolean, scope?: string): void;
            clear(scope?: string): void;
        };
    }
}

// ────────────── one-time style injection ──────────────
if (!document.getElementById('ft3d-overrun-style')) {
    const style = document.createElement('style');
    style.id = 'ft3d-overrun-style';
    style.textContent = `
@keyframes ft3d-overrun-pulse {
    0%, 100% { transform: translate(-50%, -50%) scale(1); }
    50%      { transform: translate(-50%, -50%) scale(1.16); }
}
.ft3d-overrun-icon {
    position: absolute;
    left: 50%; top: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 1001;
    filter: drop-shadow(0 0 2px #000) drop-shadow(0 0 6px #ff8c00);
    animation: ft3d-overrun-pulse 1.1s ease-in-out infinite;
}
.ft3d-overrun-icon.blocked {
    filter: drop-shadow(0 0 2px #000) drop-shadow(0 0 7px #ffb703);
}
.ft3d-overrun-icon img { display: block; }
/* "No overrun charges" block on the drop preview — amber, modeled on .drop-preview.nudged. */
.drop-preview.overrun-blocked {
    border-color: #ffb703 !important;
    border-style: dashed !important;
    background: rgba(255, 183, 3, 0.22) !important;
    box-shadow: 0 0 16px rgba(255, 183, 3, 0.85) !important;
}
`;
    document.head.appendChild(style);
}

// ────────────── per-scope state ──────────────
// scope key: 'local' for the user's own drag; the remote user_id for each incoming move preview.
interface ScopeState {
    candidates: OverrunCandidate[] | null;
    candidatesForMoverId: string | null;
    decoratedIds: Set<string>;
}
const _scopes = new Map<string, ScopeState>();
function _scope(scope: string): ScopeState {
    let s = _scopes.get(scope);
    if (!s) {
        s = { candidates: null, candidatesForMoverId: null, decoratedIds: new Set() };
        _scopes.set(scope, s);
    }
    return s;
}

// ────────────── diagnostics ──────────────
function _dbg(label: string, payload: Record<string, unknown>): void {
    if (typeof ftDebugLog === 'function') {
        ftDebugLog('ft3d-overrun: ' + label, () => payload);
    }
}


// ────────────── accessors ──────────────
// Dual-shape read: prefer the CURRENT row shape (build_encounter_creature_arr → bare scalars under
// `encounter_creatures`, what token-data.ts / TokenDragController use), and fall back to the legacy
// `encounter_creature_arr` [0]-wrapped sub-object that ft3d-oa-threat.ts reads. Map rows have carried both
// across the 2026-06 reshape, so reading both keeps gating correct whichever is populated.
function _record(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function _creatures(widget: OverrunWidget): unknown[] {
    const encounter = _record(widget.encounterArr);
    return Array.isArray(encounter?.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [];
}

function _ec(c: unknown, key: string): unknown {
    const creature = _record(c);
    const v = encounterCreatureField(creature?.encounter_creatures, key);
    if (v !== undefined && v !== null && v !== '') return v;
    const legacy = _record(creature?.encounter_creature_arr);
    const leg = legacy?.[key];
    return Array.isArray(leg) ? leg[0] : leg;
}
const _id       = (c: unknown): string  => String(_ec(c, 'encounter_creature_id') ?? '');
const _team     = (c: unknown): string  => String(_ec(c, 'team') ?? '');
const _isAlive  = (c: unknown): boolean => _ec(c, 'encounter_creature_dead') != 1;
const _private  = (c: unknown): boolean => parseInt(String(_ec(c, 'encounter_creature_private') ?? 0), 10) === 1;
const _inCombat = (w: OverrunWidget): boolean => Number(encounterField(w.encounterArr, 'in_combat') ?? 0) === 1;

// ────────────── geometry ──────────────
function _syntheticMoverAt(moverObj: unknown, x: number, z: number): Record<string, unknown> {
    const mover = _record(moverObj) ?? {};
    return Object.assign({}, mover, {
        position: Object.assign({}, _record(mover.position) ?? {}, { x_pos: x, z_pos: z }),
    });
}

// Axis-aligned footprint overlap — matches PHP do_rectangles_overlap / grid-math.rectanglesOverlap.
function _overlap(
    r1: CreatureRect,
    r2: CreatureRect,
): boolean {
    return !(r1.right <= r2.left || r1.left >= r2.right ||
             r1.top  <= r2.bottom || r1.bottom >= r2.top);
}

// Signed separation of a point from `rect`: positive = gap to the nearest edge, negative =
// shortest distance out through any edge. Mirror of PHP get_rectangle_signed_separation().
function _signedSeparation(x: number, z: number, rect: CreatureRect): number {
    const dx = Math.max(rect.left - x, 0, x - rect.right);
    const dz = Math.max(rect.bottom - z, 0, z - rect.top);

    if (dx > 0 || dz > 0) return Math.hypot(dx, dz);

    return -Math.min(x - rect.left, rect.right - x, z - rect.bottom, rect.top - z);
}

// Minkowski sum reducing rect-vs-rect to point-vs-rect, using per-side offsets measured from
// the anchor the mover footprint was built at — so an off-centre footprint can't skew the gate.
// Mirror of PHP get_rectangle_minkowski_sum().
function _minkowskiSum(rect: CreatureRect, moverRect: CreatureRect, anchorX: number, anchorZ: number): CreatureRect {
    const leftOffset   = anchorX - moverRect.left;
    const rightOffset  = moverRect.right - anchorX;
    const bottomOffset = anchorZ - moverRect.bottom;
    const topOffset    = moverRect.top - anchorZ;

    return {
        left:   rect.left   - rightOffset,
        right:  rect.right  + leftOffset,
        bottom: rect.bottom - topOffset,
        top:    rect.top    + bottomOffset,
    };
}

// Does this step press the mover DEEPER into `hostileRect`? Measured against the rectangle,
// not its centre point (2026-07-19) — a swarm's occupied width spreads horizontally with
// swarm_width_lanes, so a wide band's centroid sits far in from either end and a mover
// sliding PARALLEL to the frontage kept "approaching" the centroid while moving away from
// the swarm itself.
//
// "Deeper" = the minimum translation that would separate the footprints grew. That is not
// globally monotone (which edge is limiting can switch mid-path, so a slide holds constant
// only while the same edge stays closest — past an end-cap, crossing inward does count as
// pressing in, which is correct: it engages more of the band). The gate only needs the
// weaker guarantee that leaving the footprint never increases the depth. The epsilon keeps
// a depth-preserving step — the mid-frontage slide behind the original bug — from tipping
// into a spurious trigger on float noise.
// Mirror of PHP does_step_press_into_rectangle() / PRESS_IN_EPSILON.
const PRESS_IN_EPSILON = 1e-9;

function _pressesInto(
    moverRect: CreatureRect,
    hostileRect: CreatureRect,
    prev: GridPoint,
    cur: GridPoint,
): boolean {
    // moverRect was built at `cur`; the footprint is constant across a step.
    const inflated = _minkowskiSum(hostileRect, moverRect, cur.x, cur.z);

    return _signedSeparation(cur.x, cur.z, inflated)
         < _signedSeparation(prev.x, prev.z, inflated) - PRESS_IN_EPSILON;
}

// Path sample spacing, in FEET. Matches the server's generate_euclidean_path_positions(),
// which walks ~1-ft steps — so the step we flag as the first trigger is the same step the
// server halts on, and the blocked snap-point (samples[i-1]) lands on the same spot.
//
// Corrected 2026-07-19: this used to compute `hypot(dx, dz) * FEET_PER_GRID`. Despite the
// "grid" naming, map coordinates here are already in FEET — origin/preview come from the
// drag controller's gridX/gridZ, the same values fed to x_pos and pixelFromGridX/Z (see the
// note in AOEManager). Multiplying by 5 inflated the path length 5x, so `stepFt = 2.5`
// actually sampled every ~0.5 ft. Removing the multiply alone would have swung it the other
// way to 2.5 ft — coarser than the server, able to step over a trigger entirely — so the
// spacing moves to 1 ft in the same change.
const SAMPLE_STEP_FT = 1;

function _segmentSamples(origin: GridPoint, preview: GridPoint): GridPoint[] {
    const dx = preview.x - origin.x;
    const dz = preview.z - origin.z;
    const pathFt = Math.hypot(dx, dz);
    const n = Math.max(2, Math.ceil(pathFt / SAMPLE_STEP_FT) + 1);
    const pts: GridPoint[] = new Array(n);
    for (let i = 0; i < n; i++) {
        const t = i / (n - 1);
        pts[i] = { x: origin.x + dx * t, z: origin.z + dz * t };
    }
    return pts;
}

// ────────────── candidate gating ──────────────
// A creature that cannot brace or reposition cannot STOP an overrun — the mover passes straight through.
// Applied-only read (hasLayeredStatusEffect, not hasStatusEffect) to match PHP has_status_effect() and
// avoid a permanent-status desync. Mirror of overrun_status_gate.php's creature_cannot_stop_overrun().
function _cannotStopOverrun(c: unknown): boolean {
    if (typeof hasLayeredStatusEffect !== 'function') return false;
    for (const id of CANNOT_STOP_OVERRUN_STATUS_IDS) {
        if (hasLayeredStatusEffect(c, id)) return true;
    }
    return false;
}

function _computeCandidates(widget: OverrunWidget, moverObj: unknown): OverrunCandidate[] {
    if (!_inCombat(widget)) return [];
    const arr = _creatures(widget);
    const moverId = _id(moverObj);
    const moverTeam = _team(moverObj);
    const opposing: (a: string, b: string) => boolean =
        (typeof isOpposingTeamPair === 'function') ? isOpposingTeamPair : () => false;
    const out: OverrunCandidate[] = [];
    for (const c of arr) {
        if (!c) continue;
        if (_id(c) === moverId) continue;
        if (!_isAlive(c)) continue;
        if (_private(c)) continue;
        if (!opposing(moverTeam, _team(c))) continue;
        if (_cannotStopOverrun(c)) continue; // incapacitated / pinned → not an overrun obstacle
        out.push({
            id: _id(c),
            creatureObj: c,
            rect: widget.getObjectRectangle(c),
            triggerStep: -1,
        });
    }
    return out;
}

// ────────────── decoration ──────────────
function _cssEsc(s: string): string {
    return (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(s) : s.replace(/(["'\\\[\]])/g, '\\$1');
}

function _setIcon(scope: string, id: string, on: boolean, blocked: boolean): void {
    const host = document.getElementById('creature_' + id);
    if (!host) return;
    const existing = host.querySelector<HTMLElement>(`.ft3d-overrun-icon[data-overrun-scope="${_cssEsc(scope)}"]`);
    if (on) {
        let el = existing;
        if (!el) {
            el = document.createElement('div');
            el.className = 'ft3d-overrun-icon';
            el.dataset.overrunScope = scope;
            el.innerHTML = (typeof iconMap === 'function') ? iconMap('overrun', 26, 26, 'Overrun') : '';
            host.appendChild(el);
        }
        el.classList.toggle('blocked', blocked);
    } else if (existing) {
        existing.remove();
    }
}

// Remove every icon for a scope across the whole document (covers tokens that scrolled / re-rendered).
function _clearScopeIcons(scope: string): void {
    document.querySelectorAll(`.ft3d-overrun-icon[data-overrun-scope="${_cssEsc(scope)}"]`).forEach((el) => el.remove());
}

// Set this scope's decorated set to exactly `ids`, adding/removing icons idempotently.
function _applyDecorations(st: ScopeState, scope: string, ids: string[], blocked: boolean): Set<string> {
    const next = new Set(ids.filter(Boolean));
    for (const id of next) _setIcon(scope, id, true, blocked);
    for (const id of st.decoratedIds) {
        if (!next.has(id)) _setIcon(scope, id, false, false);
    }
    st.decoratedIds = next;
    return next;
}

// ────────────── public API ──────────────
function update(widget: OverrunWidget, moverObj: unknown, origin: GridPoint, preview: GridPoint, scope = 'local'): OverrunResult {
    const st = _scope(scope);
    const INACTIVE: OverrunResult = { overrunActive: false, blocked: false, snap: null, ids: [] };

    if (!widget || !moverObj || !origin || !preview) {
        clear(scope);
        return INACTIVE;
    }

    const moverId = _id(moverObj);
    if (st.candidates === null || st.candidatesForMoverId !== moverId) {
        st.candidates = _computeCandidates(widget, moverObj);
        st.candidatesForMoverId = moverId;
    } else {
        for (const c of st.candidates) c.triggerStep = -1; // reset per-frame trigger marks
    }

    const cands = st.candidates;
    if (!cands || cands.length === 0) {
        if (st.decoratedIds.size) _applyDecorations(st, scope, [], false);
        return INACTIVE;
    }

    // Single forward pass over the path. The first step that overlaps AND presses in on ANY hostile is
    // where the server would resolve the first overrun (and halt, if uncharged). Mirrors the server's
    // does_step_press_into_rectangle() gate.
    const samples = _segmentSamples(origin, preview);
    for (let i = 1; i < samples.length; i++) {
        const cur = samples[i];
        const prev = samples[i - 1];
        if (!cur || !prev) continue;
        const moverRect = widget.getObjectRectangle(_syntheticMoverAt(moverObj, cur.x, cur.z));
        for (const cand of cands) {
            if (cand.triggerStep >= 0) continue; // already triggered earlier on the path
            if (!_overlap(moverRect, cand.rect)) continue;
            if (_pressesInto(moverRect, cand.rect, prev, cur)) cand.triggerStep = i;
        }
    }

    const triggered = cands.filter((c) => c.triggerStep >= 0);
    const charges = overrunChargesRemainingOf(moverObj);
    const blocked = charges !== undefined && charges <= 0 && triggered.length > 0;

    // Which tokens to mark + (when blocked) the stop-point. The server halts at the FIRST creature, so when
    // blocked we mark only the creature(s) at the earliest trigger step and snap to the cell before it.
    let toDecorate: OverrunCandidate[];
    let snap: GridPoint | null = null;
    if (blocked) {
        const firstStep = Math.min(...triggered.map((c) => c.triggerStep));
        toDecorate = triggered.filter((c) => c.triggerStep === firstStep);
        const safe = samples[Math.max(0, firstStep - 1)] ?? origin;
        snap = { x: safe.x, z: safe.z };
    } else {
        toDecorate = triggered;
    }

    const next = _applyDecorations(st, scope, toDecorate.map((c) => c.id), blocked);

    if (next.size) _dbg('overrun', { scope, ids: [...next], blocked, charges });

    return { overrunActive: triggered.length > 0, blocked, snap, ids: [...next] };
}

// Remote path: decorate a broadcast set of overrun targets directly (no geometry).
function decorate(ids: string[], blocked: boolean, scope = 'local'): void {
    const st = _scope(scope);
    _applyDecorations(st, scope, Array.isArray(ids) ? ids : [], !!blocked);
}

function clear(scope = 'local'): void {
    const st = _scopes.get(scope);
    if (st) {
        for (const id of st.decoratedIds) _setIcon(scope, id, false, false);
        st.decoratedIds = new Set();
        st.candidates = null;
        st.candidatesForMoverId = null;
    }
    // Belt-and-suspenders: sweep any orphaned icons for this scope.
    _clearScopeIcons(scope);
}

window.FT3D_OVERRUN = { update, decorate, clear };

_dbg('module init', { version: 1 });

export {};
